import { ethers } from 'ethers';
import { CHAINS, CONTRACTS } from '../config/constants';

// Import PuffBridge ABI (unified for both chains)
import PuffBridgeABI from '../abi/PuffBridge.json';

// Contract read utilities for direct blockchain interaction
export class ContractUtils {
  constructor() {
    this.providers = {};
    this.contracts = {};
    this.initializeProviders();
  }

  initializeProviders() {
    try {
      // Initialize providers for each chain
      this.providers[CHAINS.RONIN.id] = new ethers.providers.JsonRpcProvider(CHAINS.RONIN.rpcUrl);
      this.providers[CHAINS.BNB.id] = new ethers.providers.JsonRpcProvider(CHAINS.BNB.rpcUrl);

      // Initialize PuffBridge contract instances for both chains
      this.contracts[CHAINS.RONIN.id] = new ethers.Contract(
        CONTRACTS.RONIN.BRIDGE,
        PuffBridgeABI,
        this.providers[CHAINS.RONIN.id]
      );

      this.contracts[CHAINS.BNB.id] = new ethers.Contract(
        CONTRACTS.BNB.BRIDGE,
        PuffBridgeABI,
        this.providers[CHAINS.BNB.id]
      );
    } catch (error) {
      console.error('Failed to initialize contract providers:', error);
    }
  }

  getContract(chainId) {
    if (!this.contracts[chainId]) {
      throw new Error(`Contract not initialized for chain ${chainId}`);
    }
    return this.contracts[chainId];
  }

  getProvider(chainId) {
    if (!this.providers[chainId]) {
      throw new Error(`Provider not initialized for chain ${chainId}`);
    }
    return this.providers[chainId];
  }


  // Calculate bridge fee for a specific amount and chain
  async calculateFee(chainId, amount) {
    try {
      const contract = this.getContract(chainId);
      
      // PuffBridge has a simple bridgeFee field
      const feeWei = await contract.bridgeFee();
      
      const feeEth = ethers.utils.formatEther(feeWei);
      const amountWei = ethers.utils.parseEther(amount.toString());
      
      return {
        totalFeeEth: feeEth,
        totalFeeUsd: '0', // PuffBridge doesn't calculate USD price
        networkFeeEth: feeEth,
        networkFeeUsd: '0',
        gasFeeEth: '0', // Gas fee is separate from bridge fee
        gasFeeUsd: '0',
        feeWei: feeWei.toString(),
        feeEth: feeEth,
        totalAmountWei: amountWei.add(feeWei).toString(),
        totalAmountEth: ethers.utils.formatEther(amountWei.add(feeWei))
      };
    } catch (error) {
      console.error(`Failed to calculate fee for chain ${chainId}:`, error);
      throw new Error(`Failed to calculate fee: ${error.message}`);
    }
  }

  // Get time delay for a specific amount (PuffBridge doesn't have time delays)
  async getTimeDelay(chainId, amount) {
    try {
      // PuffBridge doesn't implement time delays like the old bridge
      // All transactions are processed immediately once validators approve
      return {
        delaySeconds: 0,
        delayMinutes: 0,
        delayHours: 0,
        formatted: 'Instant (after validator approval)'
      };
    } catch (error) {
      console.error(`Failed to get time delay for chain ${chainId}:`, error);
      throw new Error(`Failed to get time delay: ${error.message}`);
    }
  }

  // Check if bridge is paused
  async getBridgePauseStatus(chainId) {
    try {
      const contract = this.getContract(chainId);
      const isPaused = await contract.paused();
      
      return {
        isPaused,
        depositsActive: !isPaused, // PuffBridge uses single pause state
        pauseReason: isPaused 
          ? 'Bridge operations are temporarily paused for maintenance'
          : null
      };
    } catch (error) {
      console.error(`Failed to get pause status for chain ${chainId}:`, error);
      throw new Error(`Failed to get pause status: ${error.message}`);
    }
  }

  // Get bridge status and basic info
  async getBridgeStatus(chainId) {
    try {
      const contract = this.getContract(chainId);
      const [isPaused, validatorInfo] = await Promise.all([
        contract.paused(),
        contract.getValidatorInfo()
      ]);
      
      const [validators, validatorThreshold] = validatorInfo;
      
      return {
        isPaused,
        depositsActive: !isPaused, // PuffBridge uses single pause state
        requiredValidations: parseInt(validatorThreshold.toString()),
        validatorCount: validators.length,
        pauseReason: isPaused 
          ? 'Bridge operations are temporarily paused for maintenance'
          : null
      };
    } catch (error) {
      console.error(`Failed to get bridge status for chain ${chainId}:`, error);
      throw new Error(`Failed to get bridge status: ${error.message}`);
    }
  }


  // Get current native currency price (PuffBridge doesn't track prices)
  async getNativeCurrencyPrice(chainId) {
    try {
      // PuffBridge doesn't implement price tracking
      // Return a default value or fetch from external source if needed
      return 0;
    } catch (error) {
      console.error(`Failed to get native currency price for chain ${chainId}:`, error);
      throw new Error(`Failed to get native currency price: ${error.message}`);
    }
  }

  // Get network status including latest block and connection
  async getNetworkStatus(chainId) {
    try {
      const provider = this.getProvider(chainId);
      const [network, blockNumber, gasPrice] = await Promise.all([
        provider.getNetwork(),
        provider.getBlockNumber(),
        provider.getGasPrice()
      ]);
      
      return {
        chainId: network.chainId,
        name: network.name,
        blockNumber,
        gasPrice: ethers.utils.formatUnits(gasPrice, 'gwei'),
        connected: true
      };
    } catch (error) {
      console.error(`Failed to get network status for chain ${chainId}:`, error);
      return {
        chainId,
        connected: false,
        error: error.message
      };
    }
  }

  // Estimate gas for bridge operations
  async estimateGas(chainId, operation, amount, userAddress) {
    try {
      console.log('estimateGas', chainId, operation, amount, userAddress);
      const provider = this.getProvider(chainId);
      const contract = this.getContract(chainId);
      
      let gasEstimate;
      const amountWei = ethers.utils.parseEther(amount.toString());
      
      // PuffBridge uses unified lockOrBurn function
      if (operation === 'bridge' || operation === 'lock' || operation === 'burn') {
        gasEstimate = await contract.estimateGas.lockOrBurn(amountWei, userAddress);
      } else {
        throw new Error(`Unsupported operation: ${operation} for chain ${chainId}`);
      }
      
      const gasPrice = await provider.getGasPrice();
      const gasCost = gasEstimate.mul(gasPrice);
      
      return {
        gasLimit: gasEstimate.toString(),
        gasPrice: ethers.utils.formatUnits(gasPrice, 'gwei'),
        gasCostWei: gasCost.toString(),
        gasCostEth: ethers.utils.formatEther(gasCost)
      };
    } catch (error) {
      console.error(`Failed to estimate gas for ${operation} on chain ${chainId}:`, error);
      throw new Error(`Failed to estimate gas: ${error.message}`);
    }
  }
}

// Create and export singleton instance
export const contractUtils = new ContractUtils();

// Export individual utility functions for convenience
export const calculateFee = (chainId, amount) => contractUtils.calculateFee(chainId, amount);
export const getTimeDelay = (chainId, amount) => contractUtils.getTimeDelay(chainId, amount);
export const getBridgePauseStatus = (chainId) => contractUtils.getBridgePauseStatus(chainId);
export const getBridgeStatus = (chainId) => contractUtils.getBridgeStatus(chainId);
export const getNativeCurrencyPrice = (chainId) => contractUtils.getNativeCurrencyPrice(chainId);
export const getNetworkStatus = (chainId) => contractUtils.getNetworkStatus(chainId);
export const estimateGas = (chainId, operation, amount, userAddress) => 
  contractUtils.estimateGas(chainId, operation, amount, userAddress);

export default contractUtils;